/** Password-flow pagina's — één file voor: /forgot-password, /reset-password, /activate.
 *  Elk maakt eigen gebruik van dezelfde generieke error-styling en generic
 *  server-messages (voorkomt account-enumeration).
 */
import { useEffect, useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { CheckCircle2, KeyRound, Mail, ShieldCheck } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function passwordStrength(pw) {
  let s = 0;
  if (pw.length >= 10) s++;
  if (pw.length >= 14) s++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) s++;
  if (/[0-9]/.test(pw)) s++;
  if (/[^A-Za-z0-9]/.test(pw)) s++;
  return Math.min(s, 4);
}

function StrengthBar({ pw }) {
  const s = passwordStrength(pw);
  const label = ["Te zwak", "Zwak", "Redelijk", "Sterk", "Zeer sterk"][s];
  const color = ["#dc2626", "#f59e0b", "#eab308", "#10b981", "#059669"][s];
  return (
    <div className="mt-2" data-testid="pw-strength">
      <div className="flex gap-1">
        {[0, 1, 2, 3].map(i => (
          <div key={i} className="h-1 flex-1 rounded" style={{ background: i < s ? color : "#e5e7eb" }} />
        ))}
      </div>
      <div className="text-[11px] mt-1" style={{ color }}>{pw ? label : "Minimaal 10 tekens"}</div>
    </div>
  );
}

function Shell({ title, subtitle, icon: Icon, children }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[color:var(--sage-100)] to-white p-4">
      <Card className="w-full max-w-md p-8 bg-white shadow-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-[color:var(--sage-100)] flex items-center justify-center">
            <Icon className="h-5 w-5 text-[color:var(--sage)]" />
          </div>
          <div>
            <h1 className="font-display text-xl tracking-tight">{title}</h1>
            {subtitle && <p className="text-xs text-neutral-500">{subtitle}</p>}
          </div>
        </div>
        {children}
      </Card>
    </div>
  );
}

export function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/auth/forgot-password`, { email });
      setSent(true);
      toast.success(r.data.message);
    } catch (e) { toast.error("Er ging iets mis, probeer opnieuw"); }
    finally { setBusy(false); }
  };
  return (
    <Shell title="Wachtwoord vergeten" subtitle="Vul je e-mailadres in, we sturen instructies." icon={Mail}>
      {sent ? (
        <div className="space-y-4">
          <div className="p-3 rounded border border-emerald-200 bg-emerald-50 text-sm text-emerald-900">
            <CheckCircle2 className="h-4 w-4 inline mr-1" />
            Als dit e-mailadres bij ons bekend is, ontvang je een e-mail met instructies. Controleer ook je spam-folder.
          </div>
          <Link to="/login" className="block text-center text-sm text-[color:var(--sage)] underline">Terug naar login</Link>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <Label htmlFor="email">E-mailadres</Label>
            <Input id="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} data-testid="forgot-email" />
          </div>
          <Button type="submit" disabled={busy} className="w-full" style={{ background: "var(--sage)" }} data-testid="forgot-submit">
            {busy ? "Verzenden…" : "Instructies verzenden"}
          </Button>
          <Link to="/login" className="block text-center text-sm text-neutral-500 underline">Terug naar login</Link>
        </form>
      )}
    </Shell>
  );
}

export function ResetPasswordPage() {
  const loc = useLocation(); const nav = useNavigate();
  const token = new URLSearchParams(loc.search).get("token") || "";
  const [state, setState] = useState({ loading: true, valid: false, email: null, error: null });
  const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!token) { setState({ loading: false, valid: false, error: "Geen token gevonden" }); return; }
    axios.post(`${API_URL}/api/auth/verify-token`, { token, type: "reset" })
      .then(r => setState({ loading: false, valid: true, email: r.data.email }))
      .catch(e => setState({ loading: false, valid: false, error: e.response?.data?.detail || "Link ongeldig" }));
  }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return toast.error("Wachtwoorden komen niet overeen");
    if (pw.length < 10) return toast.error("Wachtwoord moet minimaal 10 tekens zijn");
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/auth/reset-password`, { token, new_password: pw });
      toast.success("Wachtwoord aangepast — log opnieuw in");
      nav("/login");
    } catch (e) { toast.error(e.response?.data?.detail || "Reset mislukt"); }
    finally { setBusy(false); }
  };
  if (state.loading) return <Shell title="Wachtwoord opnieuw instellen" icon={KeyRound}><p className="text-sm">Laden…</p></Shell>;
  if (!state.valid) return (
    <Shell title="Link ongeldig" icon={KeyRound}>
      <p className="text-sm text-neutral-700">{state.error}</p>
      <Link to="/forgot-password" className="block mt-4 text-center text-sm text-[color:var(--sage)] underline">Nieuwe link aanvragen</Link>
    </Shell>
  );
  return (
    <Shell title="Nieuw wachtwoord instellen" subtitle={state.email} icon={KeyRound}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="pw">Nieuw wachtwoord</Label>
          <Input id="pw" type="password" required value={pw} onChange={e => setPw(e.target.value)} data-testid="reset-pw" />
          <StrengthBar pw={pw} />
        </div>
        <div>
          <Label htmlFor="pw2">Bevestig wachtwoord</Label>
          <Input id="pw2" type="password" required value={pw2} onChange={e => setPw2(e.target.value)} data-testid="reset-pw2" />
        </div>
        <Button type="submit" disabled={busy || pw !== pw2 || pw.length < 10} className="w-full" style={{ background: "var(--sage)" }} data-testid="reset-submit">
          {busy ? "Opslaan…" : "Wachtwoord instellen"}
        </Button>
      </form>
    </Shell>
  );
}

export function ActivatePage() {
  const loc = useLocation(); const nav = useNavigate();
  const token = new URLSearchParams(loc.search).get("token") || "";
  const [state, setState] = useState({ loading: true, valid: false, error: null });
  const [naam, setNaam] = useState(""); const [pw, setPw] = useState(""); const [pw2, setPw2] = useState("");
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!token) { setState({ loading: false, valid: false, error: "Geen token gevonden" }); return; }
    axios.post(`${API_URL}/api/auth/verify-token`, { token, type: "activation" })
      .then(r => { setState({ loading: false, valid: true, ...r.data }); setNaam(r.data.name || ""); })
      .catch(e => setState({ loading: false, valid: false, error: e.response?.data?.detail || "Link ongeldig" }));
  }, [token]);
  const submit = async (e) => {
    e.preventDefault();
    if (!accept) return toast.error("Accepteer de voorwaarden");
    if (pw !== pw2) return toast.error("Wachtwoorden komen niet overeen");
    if (pw.length < 10) return toast.error("Wachtwoord moet minimaal 10 tekens zijn");
    setBusy(true);
    try {
      await axios.post(`${API_URL}/api/auth/activate`, { token, password: pw, name: naam, accept_tos: true }, { withCredentials: true });
      toast.success("Account geactiveerd — welkom!");
      const role = state.role;
      nav(role === "ouder" ? "/portaal" : "/app");
    } catch (e) { toast.error(e.response?.data?.detail || "Activatie mislukt"); }
    finally { setBusy(false); }
  };
  if (state.loading) return <Shell title="Account activeren" icon={ShieldCheck}><p className="text-sm">Laden…</p></Shell>;
  if (!state.valid) return (
    <Shell title="Link ongeldig" icon={ShieldCheck}>
      <p className="text-sm text-neutral-700">{state.error}</p>
      <p className="text-xs text-neutral-500 mt-2">Vraag de uitnodiging opnieuw aan bij je beheerder.</p>
      <Link to="/login" className="block mt-4 text-center text-sm text-[color:var(--sage)] underline">Naar login</Link>
    </Shell>
  );
  return (
    <Shell title="Account activeren" subtitle={state.email} icon={ShieldCheck}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label htmlFor="name">Volledige naam</Label>
          <Input id="name" required value={naam} onChange={e => setNaam(e.target.value)} data-testid="activate-name" />
        </div>
        <div>
          <Label htmlFor="pw">Wachtwoord (min 10 tekens)</Label>
          <Input id="pw" type="password" required value={pw} onChange={e => setPw(e.target.value)} data-testid="activate-pw" />
          <StrengthBar pw={pw} />
        </div>
        <div>
          <Label htmlFor="pw2">Bevestig wachtwoord</Label>
          <Input id="pw2" type="password" required value={pw2} onChange={e => setPw2(e.target.value)} data-testid="activate-pw2" />
        </div>
        <div className="flex items-start gap-2">
          <Checkbox id="accept" checked={accept} onCheckedChange={setAccept} data-testid="activate-accept" />
          <label htmlFor="accept" className="text-xs text-neutral-700 leading-snug">
            Ik ga akkoord met de gebruikersvoorwaarden en privacyverklaring van Webster en mijn organisatie.
          </label>
        </div>
        <Button type="submit" disabled={busy || !accept || pw !== pw2 || pw.length < 10} className="w-full" style={{ background: "var(--sage)" }} data-testid="activate-submit">
          {busy ? "Activeren…" : "Account activeren"}
        </Button>
      </form>
    </Shell>
  );
}
